import type { PlayerEnv } from './env';
import type { EVENT_TYPE } from './events';
import type { PlayerType } from './player';

// should move this to wherever studio env logic lives?
export const STUDIO_ENV = {
  STUDIO_PROD: 'studio-prod',
  STUDIO_INT: 'studio-int',
  STUDIO_LOCAL: 'studio-local',
  NOT_STUDIO: 'not-studio',
} as const;
export type StudioEnv = (typeof STUDIO_ENV)[keyof typeof STUDIO_ENV];

export const MODE = {
  DEFAULT: 'default',
  LIVE: 'live',
  BACKGROUND: 'background',
} as const;
export type Mode = (typeof MODE)[keyof typeof MODE];

export const isModePredicate = (str: string): str is Mode => Object.values<string>(MODE).includes(str);

export const NATIVE_FULLSCREEN_OPTION = {
  ALWAYS: 'always',
  DEFAULT: 'default',
  NEVER: 'never',
} as const;
export type NativeFullscreenOption = (typeof NATIVE_FULLSCREEN_OPTION)[keyof typeof NATIVE_FULLSCREEN_OPTION];
export const isNativeFullscreenOptionPredicate = (str: string): str is NativeFullscreenOption =>
  Object.values<string>(NATIVE_FULLSCREEN_OPTION).includes(str);

export type AttributeEncodedManifests = {
  hls: string;
  dash: string;
};

export const MIN_HOTSPOT_DURATION_MS = 3200;

export const isEncodedManifestsPredicate = (value: Record<string, unknown>): value is AttributeEncodedManifests => {
  return typeof value.hls === 'string' && typeof value.dash === 'string';
};

export class StudioUrl {
  static readonly PROD = 'https://studio.resi.io';
  static readonly INT = 'https://int-studio.resi.io';
  static readonly LOCAL = 'http://localhost:3000';
}

export class AttributeDataOptions {
  static readonly AUDIO_ONLY = 'data-audio-only';
  static readonly AUTOPLAY = 'data-autoplay';
  static readonly BACKGROUND = 'data-background';
  static readonly BITRATE_LIMIT = 'data-bitrate-limit';
  static readonly CALL_TO_ACTION_BUTTON = 'data-call-to-action-button';
  static readonly CHUNKED_TRANSFER = 'data-chunked-transfer';
  static readonly CONTROLS = 'data-controls';
  static readonly DEBUG = 'data-debug';
  static readonly DISABLE_ANALYTICS = 'data-disable-analytics';
  static readonly DISABLE_THUMBNAIL = 'data-disable-thumbnail';
  static readonly ENCODED_MANIFEST = 'data-encoded-manifest';
  static readonly ENCODED_MANIFESTS = 'data-encoded-manifests';
  static readonly EMBED_ID = 'data-embed-id';
  static readonly ENV = 'data-env';
  static readonly IFRAME_ID = 'data-iframe-id';
  static readonly IS_IOS = 'data-is-ios';
  static readonly LOOP = 'data-loop';
  static readonly MANIFEST = 'data-manifest';
  static readonly MANIFEST_TYPE = 'data-manifest-type';
  static readonly MIN_HOTSPOT_DURATION = 'data-min-hotspot-duration';
  static readonly MODE = 'data-mode';
  static readonly MUTE = 'data-mute';
  static readonly NATIVE_FULLSCREEN = 'data-native-fullscreen';
  static readonly NO_TRANSFORM = 'data-no-transform';
  static readonly PLAYER_CONFIG_ID = 'data-player-profile-id';
  static readonly SCRUB_BAR = 'data-scrub-bar';
  static readonly START_POS = 'data-start-pos';
  static readonly START_TIME = 'data-start-time';
  static readonly STUDIO = 'data-studio';
  static readonly TYPE = 'data-type';
}

export interface AttributeOptions {
  areControlsVisible: boolean;
  bitrateLimitEnabled: boolean;
  callToActionButton: boolean;
  chunkedTransfer: Nullish<boolean>;
  disableAnalytics: boolean;
  embedId: Nullish<string>;
  eventType: Nullish<EVENT_TYPE>;
  iframeId: Nullish<string>;
  isAudioOnly: boolean;
  isAutoplay: boolean;
  isBackgroundMode: boolean;
  isDebugMode: boolean;
  isLooping: boolean;
  isMuted: boolean;
  manifest: Nullish<string>;
  manifests: Nullish<AttributeEncodedManifests>;
  manifestType: Nullish<PlayerType>;
  minHotspotDurationMs: number;
  mode: Mode;
  nativeFullscreen: NativeFullscreenOption;
  noTransform: boolean;
  playerEnv: Nullish<PlayerEnv>;
  playerConfigId: Nullish<string>;
  scrubBar: boolean;
  startPos: Nullish<number>;
  startTime: Nullish<string>;
  studioEnv: StudioEnv;
  thumbnailDisabled: boolean;
}
